import type { WidgetConfig } from "./types";

/**
 * Default base URL for the widget iframe
 */
const DEFAULT_BASE_URL = "https://app.fdback.io";

/**
 * Default and constraint values for widget dimensions
 */
const WIDGET_DIMENSIONS = {
	width: { default: 440, min: 320, max: 600 },
	height: { default: 680, min: 400, max: 900 },
} as const;

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

/**
 * Get widget dimensions from config with defaults and constraints
 */
function getWidgetDimensions(config?: WidgetConfig): {
	width: number;
	height: number;
} {
	const width = config?.width
		? clamp(
				config.width,
				WIDGET_DIMENSIONS.width.min,
				WIDGET_DIMENSIONS.width.max,
			)
		: WIDGET_DIMENSIONS.width.default;

	const height = config?.height
		? clamp(
				config.height,
				WIDGET_DIMENSIONS.height.min,
				WIDGET_DIMENSIONS.height.max,
			)
		: WIDGET_DIMENSIONS.height.default;

	return { width, height };
}

/**
 * Get base URL for the widget iframe
 * Uses config.baseUrl if provided, otherwise defaults to app.fdback.io
 */
export function getWidgetBaseUrl(config?: WidgetConfig): string {
	return config?.baseUrl ?? DEFAULT_BASE_URL;
}

/**
 * Mobile breakpoint (matches Tailwind's sm)
 */
const MOBILE_BREAKPOINT = 640;

/**
 * Check if current viewport is mobile
 */
export function isMobileViewport(): boolean {
	return window.innerWidth < MOBILE_BREAKPOINT;
}

/**
 * Normalize CSS string by removing extra whitespace
 */
function normalizeCss(css: string): string {
	return css.replace(/\s+/g, " ").trim();
}

/**
 * Iframe container styles
 */
const IFRAME_STYLES = {
	// Base styles shared by both mobile and desktop
	base: normalizeCss(`
		position: fixed;
		z-index: 2147483646;
		border: none;
		overflow: hidden;
		pointer-events: none;
	`),
	// Desktop: positioned widget with scale animation
	desktop: normalizeCss(`
		border-radius: 16px;
		box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
		opacity: 0;
		transform: translateY(10px) scale(0.95);
		transition: opacity 0.2s ease, transform 0.2s ease;
	`),
	desktopVisible: normalizeCss(`
		opacity: 1;
		transform: translateY(0) scale(1);
		pointer-events: auto;
	`),
	// Mobile: fullscreen with slide-up animation
	mobile: normalizeCss(`
		inset: 0;
		width: 100%;
		height: 100%;
		border-radius: 0;
		box-shadow: none;
		opacity: 0;
		transform: translateY(100%);
		transition: opacity 0.3s ease, transform 0.3s ease;
	`),
	mobileVisible: normalizeCss(`
		opacity: 1;
		transform: translateY(0);
		pointer-events: auto;
	`),
	iframe: normalizeCss(`
		border: none;
		width: 100%;
		height: 100%;
	`),
	bottomRight: normalizeCss(`
		bottom: 88px;
		right: 20px;
	`),
	bottomLeft: normalizeCss(`
		bottom: 88px;
		left: 20px;
	`),
	topRight: normalizeCss(`
		top: 88px;
		right: 20px;
	`),
	topLeft: normalizeCss(`
		top: 88px;
		left: 20px;
	`),
};

export interface IframeState {
	container: HTMLDivElement;
	iframe: HTMLIFrameElement;
	isReady: boolean;
	isVisible: boolean;
	isMobile: boolean;
	baseUrl: string;
	config: WidgetConfig;
	resizeHandler: () => void;
	onViewportChange?: (isMobile: boolean) => void;
}

/**
 * Build the widget URL with config params
 */
function buildWidgetUrl(config: WidgetConfig, isMobile: boolean): string {
	const params = new URLSearchParams();

	// Mode
	if (config.mode) {
		params.set("mode", config.mode);
	}

	// Tabs
	if (config.tabs && config.tabs.length > 0) {
		params.set("tabs", config.tabs.join(","));
	}

	// Primary color
	if (config.primaryColor) {
		params.set("primaryColor", config.primaryColor.replace("#", ""));
	}

	// Theme
	if (config.theme) {
		params.set("theme", config.theme);
	}

	// Mobile fullscreen mode
	if (isMobile) {
		params.set("mobile", "1");
	}

	const queryString = params.toString();
	const baseUrl = getWidgetBaseUrl(config);
	return `${baseUrl}/widget/${config.workspaceId}${queryString ? `?${queryString}` : ""}`;
}

/**
 * Apply responsive styles based on viewport and visibility state
 */
function applyResponsiveStyles(
	container: HTMLDivElement,
	config: WidgetConfig,
	isVisible: boolean,
): void {
	const isMobile = isMobileViewport();

	if (isMobile) {
		// Full screen on mobile with slide-up animation
		const styles = [
			IFRAME_STYLES.base,
			IFRAME_STYLES.mobile,
			isVisible ? IFRAME_STYLES.mobileVisible : "",
		]
			.filter(Boolean)
			.join("; ");
		container.style.cssText = styles;
	} else {
		// Positioned widget on desktop with scale animation
		const positionStyles = {
			"bottom-right": IFRAME_STYLES.bottomRight,
			"bottom-left": IFRAME_STYLES.bottomLeft,
			"top-right": IFRAME_STYLES.topRight,
			"top-left": IFRAME_STYLES.topLeft,
		};
		const position = positionStyles[config.position ?? "bottom-right"];
		const styles = [
			IFRAME_STYLES.base,
			IFRAME_STYLES.desktop,
			position,
			isVisible ? IFRAME_STYLES.desktopVisible : "",
		]
			.filter(Boolean)
			.join("; ");
		container.style.cssText = styles;

		// Set dimensions from config (with defaults and constraints)
		const { width, height } = getWidgetDimensions(config);
		container.style.width = `${width}px`;
		container.style.height = `${height}px`;
	}
}

/**
 * Create the iframe container
 */
export function createIframe(config: WidgetConfig): IframeState {
	// Create container
	const container = document.createElement("div");
	container.id = "fdback-widget-container";

	// Determine initial mobile state
	const initialIsMobile = isMobileViewport();

	// Create state object first so resize handler can reference it
	const state: IframeState = {
		container,
		iframe: null as unknown as HTMLIFrameElement,
		isReady: false,
		isVisible: false,
		isMobile: initialIsMobile,
		baseUrl: getWidgetBaseUrl(config),
		config,
		resizeHandler: () => {
			const nowMobile = isMobileViewport();
			const viewportChanged = nowMobile !== state.isMobile;

			// Update styles
			applyResponsiveStyles(container, config, state.isVisible);

			// If viewport crossed the mobile breakpoint, notify iframe and parent
			if (viewportChanged) {
				state.isMobile = nowMobile;
				sendMessageToIframe(state, {
					type: "fdback:viewportChange",
					isMobile: nowMobile,
				});
				// Notify parent widget to update launcher visibility
				state.onViewportChange?.(nowMobile);
			}
		},
	};

	// Apply initial responsive styles (hidden)
	applyResponsiveStyles(container, config, false);

	// Create iframe with mobile state
	const iframe = document.createElement("iframe");
	iframe.id = "fdback-widget-iframe";
	iframe.src = buildWidgetUrl(config, initialIsMobile);
	iframe.style.cssText = IFRAME_STYLES.iframe;
	iframe.setAttribute("allow", "clipboard-write");
	iframe.setAttribute("title", "Fdback Widget");
	state.iframe = iframe;

	container.appendChild(iframe);
	document.body.appendChild(container);

	// Handle viewport resize
	window.addEventListener("resize", state.resizeHandler);

	return state;
}

/**
 * Show the iframe
 */
export function showIframe(state: IframeState): void {
	state.isVisible = true;
	applyResponsiveStyles(state.container, state.config, true);
}

/**
 * Hide the iframe
 */
export function hideIframe(state: IframeState): void {
	state.isVisible = false;
	applyResponsiveStyles(state.container, state.config, false);
}

/**
 * Update iframe height based on content (desktop only)
 */
export function resizeIframe(state: IframeState, height: number): void {
	// Don't resize on mobile - it's fullscreen
	if (isMobileViewport()) return;

	const maxHeight = Math.min(height, window.innerHeight - 120);
	state.container.style.height = `${Math.max(300, maxHeight)}px`;
}

/**
 * Send message to iframe
 */
export function sendMessageToIframe(
	state: IframeState,
	message: Record<string, unknown>,
): void {
	if (state.iframe.contentWindow) {
		// Use specific origin instead of "*" for security
		state.iframe.contentWindow.postMessage(message, state.baseUrl);
	}
}

/**
 * Destroy the iframe
 */
export function destroyIframe(state: IframeState): void {
	window.removeEventListener("resize", state.resizeHandler);
	state.container.remove();
}
